import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import customerPortalAdminService from '../../services/customerPortalAdminService';

/**
 * Super-admin-only card (inside CustomerDetails) to manage a customer's portal
 * login: enable login (email/mobile), set/reset password, disable login.
 * A customer cannot log in until login is enabled AND a password is set.
 */
const PortalAccessCard = ({ customerId, defaultEmail, defaultMobile }) => {
  const { t } = useTranslation();
  const s = t('customers.portalAccess', { returnObjects: true });

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await customerPortalAdminService.getLoginStatus(customerId);
    if (res.success) {
      setStatus(res.data);
      setEmail(res.data.login_email || defaultEmail || '');
      setMobile(res.data.login_mobile || defaultMobile || '');
    } else {
      setError(res.error);
    }
    setLoading(false);
  }, [customerId, defaultEmail, defaultMobile]);

  useEffect(() => { if (customerId) loadStatus(); }, [customerId, loadStatus]);

  const run = async (fn, successMsg) => {
    setBusy(true); setError(''); setNotice('');
    const res = await fn();
    if (res.success) {
      setStatus(res.data);
      if (successMsg) setNotice(successMsg);
    } else {
      setError(res.error); // surfaces the 409/400 message verbatim
    }
    setBusy(false);
    return res.success;
  };

  const handleEnable = () => {
    if (!email && !mobile) { setError(s?.errors?.identifierRequired || 'Enter an email or mobile.'); return; }
    run(() => customerPortalAdminService.enableLogin(customerId, { login_email: email || undefined, login_mobile: mobile || undefined }), s?.enabled || 'Portal login enabled.');
  };
  const handleSetPassword = async () => {
    if (!password || password.length < 8) { setError(s?.errors?.passwordShort || 'Password must be at least 8 characters.'); return; }
    const okDone = await run(() => customerPortalAdminService.setPassword(customerId, password), s?.passwordSet || 'Password set.');
    if (okDone) setPassword('');
  };
  const handleDisable = () => run(() => customerPortalAdminService.disableLogin(customerId), s?.disabled || 'Portal login disabled.');

  // Resend the invite (fresh temp password). Doesn't touch login-status display.
  // 429 = every channel throttled (temp password NOT rotated); 400 = not in an
  // invite-able onboarding state.
  const handleResend = async () => {
    setBusy(true); setError(''); setNotice('');
    const res = await customerPortalAdminService.resendInvite(customerId);
    if (res.success) {
      const ch = res.data?.invited_channels || [];
      const th = res.data?.throttled_channels || [];
      if (ch.length === 0 && th.length === 0) {
        setNotice(s?.resendNoChannel || 'No channel is enabled — nothing was sent.');
      } else {
        setNotice(`${s?.resent || 'Invite re-sent'}${ch.length ? ` (${ch.join(', ')})` : ''}.`);
      }
    } else if (res.status === 429) {
      setError(s?.resendThrottled || 'Invited very recently — try again shortly. The existing password still works.');
    } else {
      setError(res.error);
    }
    setBusy(false);
  };

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };
  const enabled = status?.is_login_enabled;
  const hasPassword = status?.has_password;

  return (
    <div className="rounded-2xl border p-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.BACKGROUND, 0.35) }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'Portal access'}</h3>
        {!loading && status && (
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span style={{ color: enabled ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED }}>{enabled ? (s?.statusEnabled || 'Enabled') : (s?.statusDisabled || 'Disabled')}</span>
            {enabled && <span style={{ color: hasPassword ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING }}>· {hasPassword ? (s?.passwordSetShort || 'Password set') : (s?.noPassword || 'No password')}</span>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2" style={{ borderColor: UI_COLORS.BRAND.TERTIARY }} /></div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.email || 'Login email'}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} placeholder="name@example.com" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.mobile || 'Login mobile'}</label>
              <input type="text" value={mobile} onChange={(e) => setMobile(e.target.value)} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} placeholder="9876543210" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={handleEnable} className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap disabled:opacity-60" style={{ color: '#ffffff', backgroundColor: UI_COLORS.BRAND.TERTIARY }}>
              {enabled ? (s?.updateIdentifiers || 'Update email/mobile') : (s?.enable || 'Enable portal login')}
            </button>
            {enabled && (
              <button type="button" disabled={busy} onClick={handleResend} className="px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-60" style={{ color: UI_COLORS.BRAND.SECONDARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.SECONDARY, 0.1) }}>
                {s?.resendInvite || 'Resend invite'}
              </button>
            )}
            {enabled && (
              <button type="button" disabled={busy} onClick={handleDisable} className="px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-60" style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}>
                {s?.disable || 'Disable login'}
              </button>
            )}
          </div>

          {enabled && (
            <div className="pt-2 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{hasPassword ? (s?.resetPassword || 'Reset password') : (s?.setPassword || 'Set password')} ({s?.min8 || 'min 8 chars'})</label>
              <div className="flex gap-2">
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="flex-1 px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} placeholder="••••••••" />
                <button type="button" disabled={busy} onClick={handleSetPassword} className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap disabled:opacity-60" style={{ color: '#ffffff', backgroundColor: UI_COLORS.BRAND.SECONDARY }}>
                  {s?.save || 'Save'}
                </button>
              </div>
            </div>
          )}

          {error && <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{error}</div>}
          {notice && <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{notice}</div>}
        </div>
      )}
    </div>
  );
};

export default PortalAccessCard;
